import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, Plus, Trash2, Check, X, TrendingUp,
  DollarSign, Building2, Percent, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GOAL_ICONS = {
  monthly_cash_flow: DollarSign,
  total_portfolio_value: Building2,
  total_equity: TrendingUp,
  num_units: Building2,
  avg_cap_rate: Percent,
  avg_dscr: Target,
};

const GOAL_ORDER = [
  'monthly_cash_flow', 'total_portfolio_value', 'total_equity',
  'num_units', 'avg_cap_rate', 'avg_dscr'
];

function formatValue(value, goalType) {
  if (goalType === 'avg_cap_rate') return `${value.toFixed(1)}%`;
  if (goalType === 'avg_dscr') return `${value.toFixed(2)}x`;
  if (goalType === 'num_units') return Math.round(value).toString();
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function progressColor(pct) {
  if (pct >= 100) return { bar: 'bg-[#45C04A]', text: 'text-[#45C04A]', glow: 'shadow-[0_0_12px_rgba(0,200,83,0.3)]' };
  if (pct >= 75) return { bar: 'bg-[#45C04A]', text: 'text-[#45C04A]', glow: '' };
  if (pct >= 50) return { bar: 'bg-amber-500', text: 'text-amber-500', glow: '' };
  return { bar: 'bg-red-500', text: 'text-red-500', glow: '' };
}

function statusLabel(pct, t) {
  if (pct >= 100) return { label: t('goals.reached'), cls: 'bg-[#45C04A]/10 text-[#45C04A]' };
  if (pct >= 75) return { label: t('goals.onTrack'), cls: 'bg-[#45C04A]/10 text-[#45C04A]' };
  if (pct >= 50) return { label: t('goals.close'), cls: 'bg-amber-500/10 text-amber-500' };
  return { label: t('goals.behind'), cls: 'bg-red-500/10 text-red-500' };
}

export default function PortfolioGoals() {
  const { t } = useTranslation();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newGoalType, setNewGoalType] = useState('');
  const [newGoalValue, setNewGoalValue] = useState('');
  const [saving, setSaving] = useState(false);

  const loadGoals = useCallback(async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/portfolio/goals`, { withCredentials: true });
      setGoals(resp.data.goals || []);
    } catch {
      // Silently fail — goals section is optional enhancement
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const existingTypes = goals.map(g => g.goal_type);
  const availableTypes = GOAL_ORDER.filter(gt => !existingTypes.includes(gt));

  const handleAdd = async () => {
    if (!newGoalType || !newGoalValue || parseFloat(newGoalValue) <= 0) {
      toast.error('Please select a goal type and enter a positive target value');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portfolio/goals`, {
        goal_type: newGoalType,
        target_value: parseFloat(newGoalValue),
      }, { withCredentials: true });
      setAdding(false);
      setNewGoalType('');
      setNewGoalValue('');
      await loadGoals();
      toast.success(t('goals.save'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalType) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portfolio/goals/${goalType}`, { withCredentials: true });
      await loadGoals();
    } catch {
      toast.error('Failed to delete goal');
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden" data-testid="portfolio-goals-section">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('goals.title')}</h3>
            <p className="text-[10px] text-slate-400">{t('goals.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {goals.length > 0 && (
            <button
              data-testid="edit-goals-btn"
              onClick={() => setEditing(!editing)}
              className="text-xs font-medium text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors"
            >
              {editing ? t('goals.done') : t('goals.editGoals')}
            </button>
          )}
          {availableTypes.length > 0 && !adding && (
            <Button
              data-testid="add-goal-btn"
              onClick={() => { setAdding(true); setNewGoalType(availableTypes[0]); }}
              size="sm"
              className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFE44D] hover:to-[#FFB833] text-[#12355B] rounded-lg text-xs font-bold px-3 h-7"
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('goals.addGoal')}
            </Button>
          )}
        </div>
      </div>

      {/* Add Goal Form */}
      {adding && (
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30" data-testid="add-goal-form">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">{t('goals.goalType')}</label>
              <select
                data-testid="goal-type-select"
                value={newGoalType}
                onChange={(e) => setNewGoalType(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-[#12355B] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50"
              >
                {availableTypes.map(gt => (
                  <option key={gt} value={gt}>{t(`goals.${gt}`)}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">{t('goals.targetValue')}</label>
              <input
                data-testid="goal-target-input"
                type="number"
                value={newGoalValue}
                onChange={(e) => setNewGoalValue(e.target.value)}
                placeholder="e.g. 5000"
                min="0"
                step="any"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-[#12355B] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="save-goal-btn"
                onClick={handleAdd}
                disabled={saving}
                size="sm"
                className="bg-[#45C04A] hover:bg-[#65D95A] text-white rounded-lg h-9 px-4 font-bold"
              >
                <Check className="w-3.5 h-3.5 mr-1" />
                {t('goals.save')}
              </Button>
              <Button
                data-testid="cancel-goal-btn"
                onClick={() => { setAdding(false); setNewGoalType(''); setNewGoalValue(''); }}
                size="sm"
                variant="ghost"
                className="rounded-lg h-9 px-3 text-slate-500"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Goals List */}
      <div className="p-5">
        {goals.length === 0 && !adding ? (
          <div className="text-center py-6">
            <Target className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">{t('goals.noGoals')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => {
              const Icon = GOAL_ICONS[goal.goal_type] || Target;
              const colors = progressColor(goal.progress);
              const status = statusLabel(goal.progress, t);
              return (
                <div
                  key={goal.goal_type}
                  data-testid={`goal-card-${goal.goal_type}`}
                  className={`relative rounded-xl border border-slate-100 dark:border-slate-800 p-4 transition-all duration-300 hover:border-slate-200 dark:hover:border-slate-700 ${goal.progress >= 100 ? colors.glow : ''}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${goal.progress >= 100 ? 'bg-[#45C04A]/10' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <Icon className={`w-4 h-4 ${goal.progress >= 100 ? 'text-[#45C04A]' : 'text-slate-500 dark:text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#12355B] dark:text-white">{t(`goals.${goal.goal_type}`)}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400">{t('goals.current')}: <strong className={colors.text}>{formatValue(goal.current_value, goal.goal_type)}</strong></span>
                          <ChevronRight className="w-3 h-3 text-slate-300" />
                          <span className="text-xs text-slate-400">{t('goals.target')}: <strong className="text-[#12355B] dark:text-white">{formatValue(goal.target_value, goal.goal_type)}</strong></span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${status.cls}`}>
                        {status.label}
                      </span>
                      {editing && (
                        <button
                          data-testid={`delete-goal-${goal.goal_type}`}
                          onClick={() => handleDelete(goal.goal_type)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${colors.bar} transition-all duration-700 ease-out`}
                        style={{ width: `${Math.min(goal.progress, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-bold min-w-[44px] text-right ${colors.text}`}>
                      {goal.progress >= 100 ? '100' : goal.progress.toFixed(0)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
